// Sygnały powłoki — JEDNO źródło (MINIMAL-2026-09-18, MAPA 30 „Tabela sygnałów”).
//
// Pulpit był agregatorem: jeden ekran czytał wszystko i mówił „wymaga uwagi”. Po jego
// usunięciu sygnał żyje tam, gdzie się go naprawia, a nawigacja nosi kropkę — więc
// ktoś musi te kropki policzyć RAZ, dla całej powłoki, zamiast pozwolić czterem
// pozycjom nawigacji pytać serwer osobno.
//
// Skąd dane (stan przejściowy, jak mówi MAPA):
//   · `GET /api/dashboard` (`useDashboard`) — rendery w toku, nieudane z 24 h, sygnały
//     utworów, saldo gemów i „sprzedaż wyłączona”. Docelowo lekki endpoint sygnałów.
//   · `GET /api/publishing/connections` — konto do ponownego logowania (tylko konto
//     z dostępem; konto bez dostępu nie wysyła ANI JEDNEGO zapytania o publikację).
//   · `GET /api/campaigns` — posty czekające na zgodę (`awaiting_consent`). To ta sama
//     odpowiedź, którą czyta `lib/autopilotAccess`, współdzielona przez
//     `fetchCampaignsState()`, więc kropka nie kosztuje drugiego zapytania.
//     `GET /api/publishing/summary` ŚWIADOMIE nie jest czytane: w jego kształcie nie ma
//     ani jednego pola o zgodach (`PublishingSummary`), a odczyt, z którego nic nie
//     wynika, to koszt bez sygnału.
//
// Odczyt `/api/dashboard` jest wystawiony w kontekście, żeby ekran Edity mógł wziąć
// z niego nieudane rendery BEZ drugiego zapytania (`useShellSignals().dashboard`).
//
// KIEDY ten odczyt jest powtarzany. `useDashboard` przestaje planować pętlę, gdy zastanie
// ciche konto (`dashboardNeedsPoll` = false), a powłoka montuje się RAZ na całą sesję
// (trasa bezścieżkowa), więc bez budzenia snapshot zamarza do przeładowania strony:
// saldo, licznik renderów i kropki zostają na stanie sprzed pierwszego renderu. Budzą go
// trzy rzeczy, każda odpowiadająca realnej zmianie po stronie serwera:
//   · zmiana `user.balance` z `/auth/me` (po wydaniu gemów, doładowaniu, ponowieniu) —
//     tak samo jak robił to poprzedni Layout (`e2ed42ad^:components/Layout.tsx`),
//   · start serii (`edimi:series-launched` z Generatora) i każda zmiana listy serii
//     (`edimi:series-changed` — usunięcie, ponowienie),
//   · wejście w grupę „Edity”.
// Po pierwszym odczycie z niepustą kolejką `dashboardNeedsPoll` sam podtrzymuje pętlę.
//
// Życie kropek (MAPA, rozstrzygnięcie koordynatora 3):
//   · „Edity” — NIEOBEJRZANE nieudane rendery z 24 h; gaśnie po wejściu na `/edits`.
//     Pamięć per konto w `localStorage` (najnowszy obejrzany nieudany render), bo
//     „obejrzane” jest sprawą tej osoby i tej przeglądarki, nie serwera. Każdy dostęp
//     w try/catch: tryb prywatny rzuca.
//   · „Biblioteka” i „Publikacja” — zostają DO NAPRAWY, bo blokują pracę.
//   · Saldo gemów nie ma kropki: samo jest sygnałem (decyzja 5).

import { createContext, createElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { useDashboard } from './useDashboard'
import { useRenderConfig } from './useRenderConfig'
import { editsFromGems, lowBalance } from '../lib/gems'
import { trackNeedsAttention, trackState } from '../lib/trackState'
import type { MusicTrack } from '../lib/types'
import { fetchCampaignsState, primeAutopilotAccess, useAutopilotAccess } from '../lib/autopilotAccess'
import { fetchCampaigns } from '../lib/campaigns'
import { fetchPublishingConnections, PUBLISHING_CHANGED, type PublishingChange } from '../lib/publishing'
import { SERIES_CHANGED } from '../lib/seriesEvents'
import { PUBLISHING_PATHS } from '../components/appNav'
import type { DashboardSnapshot } from '../lib/dashboard'

export type DashboardRead = ReturnType<typeof useDashboard>

/** Generator wysyła je po `createRender` (`routes/Generator.tsx`). Nazwa stoi tutaj, bo po
 *  zdjęciu `SeriesActivityProvider` powłoka jest jedynym słuchaczem tego zdarzenia. */
export const SERIES_LAUNCHED = 'edimi:series-launched'

/** Pamięć po plakietce „Nowa”, która wyleciała razem z `SeriesActivityProvider`: nikt jej
 *  już nie czyta ani nie zapisuje, więc powłoka kasuje ją raz, zamiast zostawiać śmieć. */
const ORPHAN_UNSEEN_KEY = 'edimi.edits.unseen'

/** Cztery stany salda w powłoce (artboard 03 / 03b, decyzja 5). */
export type GemState = 'ok' | 'low' | 'zero' | 'nopay'

export interface ShellGems {
  balance: number
  state: GemState
  /** Ile editów jeszcze wyjdzie z tego salda; null = serwer nie podał ceny editu. */
  editsLeft: number | null
  /** „Doładuj” ma sens wyłącznie wtedy, gdy sprzedaż działa. */
  canTopUp: boolean
}

export interface ShellSignals {
  /** Licznik przy „Edity”: kolejka + rendery w toku. Zawsze neutralny (decyzja 4). */
  rendersInProgress: number
  editsDot: boolean
  libraryDot: boolean
  /** Kropka POZYCJI „Publikacja”: zapala ją dowolny z dwóch sygnałów publikacji. */
  publishingDot: boolean
  /** Konto do ponownego logowania — sprawa leży w Publikacja › Konta (MAPA 30, `reauthRows`). */
  publishingExpired: boolean
  /** Posty czekają na zgodę — sprawa leży w Publikacja › Planer / Kalendarz (MAPA 30). */
  publishingConsent: boolean
  gems: ShellGems
  /** Kropka dzwonka (PW, reguła 5): nieprzeczytane powiadomienia konta. Gaśnie na
   *  `/powiadomienia` od razu, a ekran po `POST /read` budzi ten sam odczyt. */
  bellDot: boolean
  /** Ten sam odczyt, z którego żyją sygnały — dla ekranów pod powłoką. */
  dashboard: DashboardRead
}

/** Najnowszy obejrzany nieudany render, per konto. */
export const SEEN_FAILED_KEY = 'edimi.edits.seenFailed.v1'

type SeenMap = Record<string, string>

function readSeen(): SeenMap {
  try {
    const raw = localStorage.getItem(SEEN_FAILED_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as SeenMap) : {}
  } catch { return {} }
}

function writeSeen(account: string, stamp: string): void {
  try {
    const next = { ...readSeen(), [account]: stamp }
    localStorage.setItem(SEEN_FAILED_KEY, JSON.stringify(next))
  } catch { /* brak pamięci: kropka zgaśnie tylko na czas tej sesji */ }
}

/** Znacznik czasu najnowszego nieudanego renderu z 24 h; pusty łańcuch = nie ma żadnego. */
export function newestFailedStamp(data: DashboardSnapshot | null): string {
  if (!data) return ''
  return data.failed_24h.reduce((newest, job) => {
    const stamp = job.finished_at || job.created_at || ''
    return stamp > newest ? stamp : newest
  }, '')
}

/** Rendery w toku: sumy z kolejki, a gdy serwer jej nie przysłał — liczniki „teraz”. */
export function rendersInFlight(data: DashboardSnapshot | null): number {
  if (!data) return 0
  const fromQueue = data.queue.reduce((n, item) => n + item.counts.rendering + item.counts.queued, 0)
  return fromQueue || data.now_counts.running + data.now_counts.queued
}

/**
 * Kropka „Biblioteka” pali się WYŁĄCZNIE przy stanie `danger` (reguła 45, tabela M2):
 * „błąd pliku” i „błąd analizy”. „Brak tekstu” jej NIE zapala — utwór instrumentalny
 * jest stanem trwałym i poprawnym, a kropka, która świeci nad czymś, czego nie da się
 * naprawić, uczy ją ignorować.
 *
 * Liczymy to TYM SAMYM rachunkiem co lista (`lib/trackState`), bo wiersze
 * `music_attention` niosą dokładnie te pola, z których lista liczy stan. Osobny
 * rachunek po polu `signal` już raz się rozjechał: zapalał kropkę na „bez słów”,
 * którego ekran Muzyki pokazywał jako stan spokojny.
 */
export function libraryNeedsHand(data: DashboardSnapshot | null): boolean {
  if (!data) return false
  return data.music_attention.some((item) => trackNeedsAttention(trackState({
    id: item.id,
    filename: item.filename,
    duration_sec: 0,
    bpm: null,
    transcript: item.transcript as MusicTrack['transcript'],
    transcript_reason: item.transcript_reason as MusicTrack['transcript_reason'],
    transcript_auto_retry: item.transcript_auto_retry,
    words: item.words,
    beats: item.beats_status === 'done',
    beats_status: item.beats_status as MusicTrack['beats_status'],
    created_at: null,
  })))
}

const ShellSignalsContext = createContext<ShellSignals | null>(null)

export function ShellSignalsProvider({ children }: { children: ReactNode }): ReactNode {
  const value = useShellSignalsValue()
  return createElement(ShellSignalsContext.Provider, { value }, children)
}

export function useShellSignals(): ShellSignals {
  const value = useContext(ShellSignalsContext)
  if (!value) throw new Error('useShellSignals() poza <ShellSignalsProvider>')
  return value
}

/**
 * Sygnaly powloki, gdy ekran MOZE stac poza powloka (wzor `useAuthOptional`).
 *
 * Edytor tekstu rysuje sie takze na lawce stanow A25, bez powloki; czyta stad
 * wylacznie saldo do okna kosztu, wiec brak powloki nie jest bledem - jest brakiem
 * odpowiedzi, a ekran ma prawo nie wiedziec.
 */
export function useShellSignalsOptional(): ShellSignals | null {
  return useContext(ShellSignalsContext)
}

function useShellSignalsValue(): ShellSignals {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const dashboard = useDashboard()
  const { config } = useRenderConfig()
  const access = useAutopilotAccess()
  const data = dashboard.data
  const refetch = dashboard.refetch

  // Pamięć po plakietce, której już nie ma: kasowana raz, nie zapisywana nigdy.
  useEffect(() => {
    try { localStorage.removeItem(ORPHAN_UNSEEN_KEY) } catch { /* brak pamięci: nic do sprzątania */ }
  }, [])

  // ── budzenie snapshotu: saldo, start serii, zmiana listy, zmiana trasy, powrót na kartę ──
  // `/auth/me` wie o nowym saldzie wcześniej niż zamrożony snapshot (Generator woła
  // `refreshUser()` po wydaniu gemów). Ref, żeby pierwszy render nie dokładał
  // drugiego odczytu przy montażu — tak samo jak stary Layout.
  const seenBalance = useRef(user?.balance)
  useEffect(() => {
    if (seenBalance.current === user?.balance) return
    seenBalance.current = user?.balance
    refetch()
  }, [user?.balance, refetch])

  useEffect(() => {
    const wake = () => { refetch() }
    window.addEventListener(SERIES_LAUNCHED, wake)
    window.addEventListener(SERIES_CHANGED, wake)
    return () => {
      window.removeEventListener(SERIES_LAUNCHED, wake)
      window.removeEventListener(SERIES_CHANGED, wake)
    }
  }, [refetch])

  // ── publikacja: dwa sygnały, zero zapytań bez dostępu ───────────────────────
  const [expired, setExpired] = useState(false)
  const [awaitingConsent, setAwaitingConsent] = useState(false)
  // WSZYSTKIE odczyty połączeń (pierwszy, route wake i event mutacji) dzielą jedną
  // rewizję. Starsza odpowiedź nie może ponownie zapalić kropki po nowszej naprawie.
  const connectionReadRevision = useRef(0)

  // Kampanie mają już własny kanał świeżości: `Campaigns` i `Calendar` przekazują
  // każdą świeżą odpowiedź przez `primeAutopilotAccess()`. Hook dostępu publikuje wtedy
  // NOWY obiekt także przy niezmienionym `enabled`, więc zależność od całego `access`
  // jest tu wersją zdarzenia. `fetchCampaignsState()` czyta właśnie zagruntowaną obietnicę
  // i nie wysyła drugiego GET-u.
  useEffect(() => {
    if (!access.enabled) { setAwaitingConsent(false); return }
    let alive = true
    fetchCampaignsState()
      .then((state) => {
        if (alive) setAwaitingConsent(state.items.some((c) => (c.awaiting_consent ?? 0) > 0))
      })
      .catch(() => { /* jw. */ })
    return () => { alive = false }
  }, [access])

  // Konta nie mają cache'u kampanii ani pollera. Czytamy je raz po uzyskaniu dostępu
  // oraz ponownie przy WEJŚCIU w inną zakładkę grupy Publikacji. To budzi wygasłą kropkę
  // po naprawie konta, bez odpytywania w tle i bez jednego requestu dla konta bez dostępu.
  const publishingRoot = PUBLISHING_PATHS.find((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? ''
  const hadPublishingAccess = useRef(false)
  const lastPublishingRoot = useRef('')
  useEffect(() => {
    const gainedAccess = access.enabled && !hadPublishingAccess.current
    const enteredRoute = access.enabled && !!publishingRoot && publishingRoot !== lastPublishingRoot.current
    hadPublishingAccess.current = access.enabled
    lastPublishingRoot.current = publishingRoot
    if (!access.enabled) { setExpired(false); return }
    if (!gainedAccess && !enteredRoute) return
    let alive = true
    let settled = false
    const current = ++connectionReadRevision.current
    fetchPublishingConnections()
      .then((connections) => {
        settled = true
        if (alive && current === connectionReadRevision.current) setExpired(connections.some((c) => c.status === 'expired'))
      })
      .catch(() => { settled = true /* nieudany odczyt nie zapala i nie gasi kropki na siłę */ })
    return () => {
      alive = false
      // Efekt odpięty PRZED odpowiedzią (StrictMode robi to przy każdym wejściu wprost na trasę)
      // oddaje znaczniki: inaczej druga tura uznałaby odczyt za zrobiony, a kropka wygasłego konta
      // nie zapaliłaby się, dopóki ktoś nie zmieni zakładki.
      if (!settled) { hadPublishingAccess.current = false; lastPublishingRoot.current = '' }
    }
  }, [access.enabled, publishingRoot])

  // Udana mutacja na aktualnej trasie nie powoduje nawigacji, więc route wake jej nie
  // zobaczy. API publikacji wysyła jedno zdarzenie PO sukcesie: połączenia czytamy
  // bezpośrednio, a kampanie pobieramy świeżo i oddajemy do wspólnego cache'u. `prime…`
  // publikuje nową wersję do efektu wyżej, bez drugiego GET-u i bez timera.
  useEffect(() => {
    if (!access.enabled) return
    let alive = true
    let campaignRevision = 0
    const changed = (event: Event) => {
      const kind = (event as CustomEvent<PublishingChange>).detail
      if (kind === 'connections') {
        const current = ++connectionReadRevision.current
        fetchPublishingConnections()
          .then((connections) => {
            if (alive && current === connectionReadRevision.current) setExpired(connections.some((c) => c.status === 'expired'))
          })
          .catch(() => { /* zachowaj ostatni wiarygodny stan */ })
        return
      }
      if (kind === 'campaigns') {
        const current = ++campaignRevision
        fetchCampaigns()
          .then((state) => { if (alive && current === campaignRevision) primeAutopilotAccess(state) })
          .catch(() => { /* zachowaj ostatni wiarygodny stan */ })
      }
    }
    window.addEventListener(PUBLISHING_CHANGED, changed)
    return () => { alive = false; window.removeEventListener(PUBLISHING_CHANGED, changed) }
  }, [access.enabled])

  // ── kropka „Edity”: nieobejrzane nieudane rendery z 24 h ────────────────────
  const account = user?.account_id != null ? String(user.account_id) : ''
  const newest = newestFailedStamp(data)
  const onEdits = pathname === '/edits' || pathname.startsWith('/edits/') || pathname.startsWith('/edit/')
  const [seen, setSeen] = useState<string>(() => readSeen()[account] ?? '')
  // Zmiana konta w tej samej karcie (wylogowanie → inne konto) czyta swoją pamięć.
  const seenAccount = useRef(account)
  if (seenAccount.current !== account) {
    seenAccount.current = account
    setSeen(readSeen()[account] ?? '')
  }
  useEffect(() => {
    if (!onEdits || !newest || !account || newest <= seen) return
    writeSeen(account, newest)
    setSeen(newest)
  }, [onEdits, newest, account, seen])

  // Każda zmiana trasy i każdy powrót na kartę czyta snapshot od nowa (nie przy montażu —
  // tylko przy przejściu). Pętla odczytu stoi, gdy nic się nie renderuje, a powiadomienia
  // z ticku (kropka dzwonka) i ekran „Edity” żyją z tego samego odczytu. Dalej jeden odczyt.
  const seenPath = useRef(pathname)
  useEffect(() => {
    if (seenPath.current === pathname) return
    seenPath.current = pathname
    refetch()
  }, [pathname, refetch])

  useEffect(() => {
    const wake = () => { if (!document.hidden) refetch() }
    document.addEventListener('visibilitychange', wake)
    return () => { document.removeEventListener('visibilitychange', wake) }
  }, [refetch])

  const balance = data?.kpis.gems.balance ?? user?.balance ?? 0
  const gemsPerEdit = config?.gemsPerEdit ?? null
  const billingAvailable = data ? data.billing_available : true

  const gems = useMemo<ShellGems>(() => {
    const editsLeft = editsFromGems(balance, gemsPerEdit)
    const low = lowBalance({ balance, gemsPerEdit, planAllowance: data?.kpis.gems.plan_allowance })
    // Kolejność stanów: zero mówi najcięższą prawdę („Render nie ruszy.”) także wtedy, gdy
    // sprzedaż stoi — a „Doładuj” i tak znika, bo o tym decyduje `canTopUp`, nie napis.
    const state: GemState = balance === 0 ? 'zero'
      : !billingAvailable ? 'nopay'
      : low?.low ? 'low'
      : 'ok'
    return { balance, state, editsLeft, canTopUp: billingAvailable }
  }, [balance, gemsPerEdit, billingAvailable, data?.kpis.gems.plan_allowance])

  // Dwa sygnały publikacji stoją osobno, bo NAPRAWIA się je na dwóch różnych
  // zakładkach (MAPA 30): wygasłe konto w „Twoje konta”, zgody w Planerze. Pozycja
  // nawigacji dalej nosi jedną kropkę — to ona prowadzi do grupy, nie do zakładki.
  const publishingExpired = access.enabled && expired
  const publishingConsent = access.enabled && awaitingConsent

  const bellDot = (data?.notifications_unread ?? 0) > 0 && pathname !== '/powiadomienia'

  return useMemo<ShellSignals>(() => ({
    rendersInProgress: rendersInFlight(data),
    editsDot: !!newest && newest > seen,
    libraryDot: libraryNeedsHand(data),
    publishingDot: publishingExpired || publishingConsent,
    publishingExpired,
    publishingConsent,
    gems,
    bellDot,
    dashboard,
  }), [data, newest, seen, publishingExpired, publishingConsent, gems, bellDot, dashboard])
}
